import {
  excludeEncryptField, hmacSHA256Key, paramEncryptConfig, sm2Mode,
  sm2PublicKey4HeaderEncrypt, sm2PublicKey4RequestEncrypt, verifySignConfig,
} from "@/constants/encryptConstants";
import { clearSessionProfile } from "@/utils/authSession";
import {
  encryptFlag, requestKey, signatureField, signValidateParamField,
  signValidateSecretField, timestampField,
} from "@/constants/httpHeaderConstants";
import { resourceIdField } from "@/constants/httpHeaderConstants";
import { getActiveResourceId } from "@/utils/resourceContext.mjs";
import { prepareHttpRequestCrypto } from '@/utils/httpCrypto.service.mjs';
import { prepareRootNavigationReload, ROOT_PATH } from '@/utils/rootNavigationState.mjs';

const AUTH_FAILURE_CODES = [401, 3006];
const PASSWORD_CHANGE_REQUIRED_CODE = 3037;
let redirectingToLogin = false;

function redirectToLogin() {
  if (redirectingToLogin) return;
  const onLoginPage = window.history?.state?.current?.split(/[?#]/, 1)[0] === '/login';
  if (onLoginPage) {
    clearSessionProfile({ notify: false, preserveNavigationTarget: true });
    prepareRootNavigationReload('/login', window);
    return;
  }
  redirectingToLogin = true;
  clearSessionProfile({ notify: false });
  prepareRootNavigationReload('/login', window);
  window.location.replace(ROOT_PATH);
}

function getResponseHeader(headers, name) {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";
  const key = Object.keys(headers).find((item) => item.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : "";
}

function appendMultipartFields(formData, values, jsonPartName, plainJson, encrypted) {
  if (typeof FormData === "undefined" || !(formData instanceof FormData) || !values) return;
  if (jsonPartName) {
    formData.set(
      jsonPartName,
      new Blob([encrypted ? JSON.stringify(values) : plainJson], { type: "application/json" })
    );
    return;
  }
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    const items = Array.isArray(value) ? value : [value];
    items.forEach((item) => {
      const normalized = typeof item === "object" ? JSON.stringify(item) : String(item);
      formData.append(key, normalized);
    });
  });
}

export function attachInterceptors(instance, options, payloadField) {
  const context = options.requestContext;
  const resourceId = context ? context.resourceId : getActiveResourceId();
  instance.interceptors.request.use(async (request) => {
    context?.assertActive?.();
    const payloadPresent = request[payloadField] !== undefined && request[payloadField] !== null;
    const source = request[payloadField] == null ? {} : JSON.parse(JSON.stringify(request[payloadField]));
    const plainJson = JSON.stringify(source);
    const prepared = await prepareHttpRequestCrypto({
      sourceJson: plainJson,
      payloadPresent,
      encryptEnabled: options.paramEncryptConfig !== undefined ? options.paramEncryptConfig : paramEncryptConfig,
      signEnabled: options.verifySignConfig !== undefined ? options.verifySignConfig : verifySignConfig,
      excludedFields: excludeEncryptField,
      requestPublicKey: sm2PublicKey4RequestEncrypt,
      headerPublicKey: sm2PublicKey4HeaderEncrypt,
      mode: sm2Mode,
      hmacKey: hmacSHA256Key,
      multipartJsonPart: options.multipartJsonPart,
      timestampField,
      secretField: signValidateSecretField,
      paramField: signValidateParamField,
    }, { signal: request.signal });
    context?.assertActive?.();
    if (prepared.encrypted) request[payloadField] = prepared.payload;
    if (prepared.encrypted) request.headers[requestKey] = prepared.requestKey;
    if (prepared.signed) request.headers[signatureField] = prepared.signature;
    if (options.multipartBody) {
      appendMultipartFields(
        request.data,
        request[payloadField],
        options.multipartJsonPart,
        plainJson,
        prepared.encrypted
      );
      delete request[payloadField];
    }
    request.headers[encryptFlag] = prepared.flagCipher;
    request.headers[timestampField] = prepared.timestamp;
    if (resourceId) request.headers[resourceIdField] = resourceId;
    return request;
  }, (error) => Promise.reject(error));

  instance.interceptors.response.use(async (response) => {
    context?.assertActive?.();
    let result = response.data;
    const contentType = getResponseHeader(response.headers, "content-type");
    const disposition = getResponseHeader(response.headers, "content-disposition");
    if (options.rawResponse && typeof Blob !== "undefined" && result instanceof Blob
        && contentType.includes("application/json") && !disposition) {
      try {
        result = JSON.parse(await result.text());
      } catch {
        // 无法解析时保留原始 Blob，交给调用方按二进制响应处理。
      }
    }
    context?.assertActive?.();
    if (!options.skipAuthRedirect && AUTH_FAILURE_CODES.includes(result?.code)) {
      redirectToLogin();
    }
    if (!options.skipAuthRedirect && result?.code === PASSWORD_CHANGE_REQUIRED_CODE
        && typeof window !== "undefined") {
      window.dispatchEvent(new Event("password-change-required"));
    }
    return options.rawResponse ? { ...response, data: result } : result;
  }, (error) => {
    if (error?.code === "ERR_NETWORK") error.message = "网络连接失败，请检查服务是否启动";
    return Promise.reject(error);
  });
}
